import React, { useState } from "react";
import { RequireStaff } from "@/components/RequireStaff";
import { useStaffDeals } from "@/hooks/useStaffDeals";
import { StaffKpiCards } from "@/components/staff/StaffKpiCards";
import { StaffDealsTable } from "@/components/staff/StaffDealsTable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Filter, RefreshCw, Inbox } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const translations = {
  en: {
    title: "Files to Sort",
    subtitle: "New submissions awaiting assignment",
    description: "These deals are not assigned to any Staff. Take one to start the review.",
    search: "Search deals...",
    priority: "Priority",
    all: "All",
    high: "High",
    normal: "Normal",
    low: "Low",
    refresh: "Refresh",
    loading: "Checking new deals..."
  },
  fr: {
    title: "Dossiers à Trier",
    subtitle: "Nouvelles soumissions en attente d'assignation",
    description: "Ces deals ne sont assignés à aucun Staff. Prenez-en un en charge pour commencer la revue.",
    search: "Rechercher...",
    priority: "Priorité",
    all: "Tous",
    high: "Haute",
    normal: "Normale",
    low: "Basse",
    refresh: "Actualiser",
    loading: "Vérification des nouveaux deals..."
  }
};

export default function StaffTriage() {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  
  const { deals, loading, kpis, takeDeal, refetch } = useStaffDeals('triage');

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          deal.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || deal.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  return (
    <RequireStaff>
      <div className="space-y-6 font-['Geist',sans-serif]">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg relative">
              <Inbox className="w-6 h-6 text-red-400" />
              {kpis.triageCount > 0 && (
                <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {kpis.triageCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <p className="text-gray-400">{t.subtitle}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => refetch?.()}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t.refresh}
          </Button>
        </header>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">{t.description}</p>
        </div>

        <StaffKpiCards kpis={kpis} view="triage" />

        <div className="flex flex-col sm:flex-row gap-3 bg-gray-800/30 p-4 rounded-xl border border-gray-700/50">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-[150px] bg-gray-900 border-gray-700 text-white">
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder={t.priority} />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-gray-300">{t.all}</SelectItem>
              <SelectItem value="high" className="text-gray-300">{t.high}</SelectItem>
              <SelectItem value="normal" className="text-gray-300">{t.normal}</SelectItem>
              <SelectItem value="low" className="text-gray-300">{t.low}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="bg-gray-800/50 rounded-xl p-8 text-center">
            <p className="text-gray-400 animate-pulse">{t.loading}</p>
          </div>
        ) : (
          <StaffDealsTable deals={filteredDeals} view="triage" onTakeDeal={takeDeal} />
        )}
      </div>
    </RequireStaff>
  );
}
